import type { IFilterParams } from "./MacroFilter";

export const DEFAULT_DATE_RANGE_OPTION_ID = "today";
export const CUSTOM_DATE_RANGE_OPTION_ID = "select_period";

type TDateRangeValidationInput = Pick<
  IFilterParams,
  "DateRangeOptionId" | "StartDate" | "EndDate"
>;

type TListValidationInput = Pick<
  IFilterParams,
  "DateRangeOptionId" | "StartDate" | "EndDate" | "OccurrenceTypeIds"
>;

export interface ITicketHistoryFilterErrors {
  endDate?: string;
  occurrenceType?: string;
  startDate?: string;
}

export const getTicketHistoryDateValidation = (
  filter: TDateRangeValidationInput,
) => {
  const isCustomPeriodSelected =
    filter.DateRangeOptionId === CUSTOM_DATE_RANGE_OPTION_ID;
  const hasStartDate = Boolean(filter.StartDate);
  const hasEndDate = Boolean(filter.EndDate);
  const hasBothDates = hasStartDate && hasEndDate;
  const isInvalidDateRange = Boolean(
    filter.StartDate && filter.EndDate && filter.StartDate > filter.EndDate,
  );
  const isStartDateRequiredInvalid = isCustomPeriodSelected && !hasStartDate;
  const isEndDateRequiredInvalid = isCustomPeriodSelected && !hasEndDate;
  const hasValidDateRange = hasBothDates && !isInvalidDateRange;

  return {
    hasBothDates,
    hasValidDateRange,
    isCustomPeriodSelected,
    isEndDateRequiredInvalid,
    isInvalidDateRange,
    isStartDateRequiredInvalid,
  };
};

export const canListTicketHistories = (filter: TListValidationInput) => {
  const { hasValidDateRange } = getTicketHistoryDateValidation(filter);

  return hasValidDateRange && filter.OccurrenceTypeIds.length > 0;
};

export const getTicketHistoryFilterErrors = ({
  filter,
  translate,
}: {
  filter: TListValidationInput;
  translate: (key: string, defaultValue?: string) => string;
}): ITicketHistoryFilterErrors => {
  const {
    isEndDateRequiredInvalid,
    isInvalidDateRange,
    isStartDateRequiredInvalid,
  } = getTicketHistoryDateValidation(filter);
  const errors: ITicketHistoryFilterErrors = {};

  if (isStartDateRequiredInvalid) {
    errors.startDate = translate(
      "chatBot.page.logs.ticketHistories.errors.start_date_required",
      "Data inicial é obrigatória",
    );
  }

  if (isInvalidDateRange) {
    errors.endDate = translate(
      "chatBot.page.logs.ticketHistories.errors.invalid_date_range",
      "Data final deve ser maior ou igual à data inicial",
    );
  } else if (isEndDateRequiredInvalid) {
    errors.endDate = translate(
      "chatBot.page.logs.ticketHistories.errors.end_date_required",
      "Data final é obrigatória",
    );
  }

  if (filter.OccurrenceTypeIds.length === 0) {
    errors.occurrenceType = translate(
      "chatBot.page.logs.ticketHistories.errors.occurrence_type_required",
      "Tipo de ocorrência é obrigatório",
    );
  }

  return errors;
};
